// Polyfills for ES2022 runtime APIs that dependencies call unconditionally and
// that crash older browsers during boot on the login page:
//   Object.hasOwn      — es-toolkit via @inertiajs/vue3 useForm (Sentry SPECTRE-ID-84)
//   Array.prototype.at — @zag-js via @ark-ui/vue / spectre-ui   (Sentry SPECTRE-ID-CM)
// Vite/esbuild transpiles syntax but does not polyfill runtime APIs, so these
// must be imported first — before any dependency code runs.
import 'core-js/actual/object/has-own';
import 'core-js/actual/array/at';

import './bootstrap';
import '../css/app.css';

import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { configureEcho } from '@laravel/echo-vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import { startErrorReporting } from '@/Support/errorReporting';

declare global {
  interface Window {
    // Set true once the SPA mounts; read by the inline boot-watchdog in app.blade.php.
    __bootOK?: boolean;
    // How far boot got, sent with the boot-watchdog's report: started (boot.ts ran), imports
    // (app.ts's imports ran), page (page component loaded), setup (Inertia handed over the app).
    __bootStage?: 'started' | 'imports' | 'page' | 'setup';
  }
}

window.__bootStage = 'imports';

const appName = import.meta.env.VITE_APP_NAME || 'Spectre Accounts Service';
const pinia = createPinia();

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: async (name) => {
    const page = await resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob<DefineComponent>('./Pages/**/*.vue'));
    page.default.layout = page.default.layout || AppLayout;
    window.__bootStage = 'page';
    return page;
  },
  setup({
    el, App, props, plugin,
  }) {
    window.__bootStage = 'setup';

    const broadcasting = props.initialPage.props.broadcasting as { key: string; cluster: string };

    configureEcho({
      broadcaster: 'pusher',
      key: broadcasting.key,
      cluster: broadcasting.cluster,
      forceTLS: true,
    });

    const app = createApp({ render: () => h(App, props) });

    // boot.ts normally started Sentry already; this covers it failing to load.
    startErrorReporting();
    Sentry.getClient()?.addIntegration(Sentry.vueIntegration({ app }));

    app
      .use(plugin)
      .use(pinia)
      .mount(el);

    // Signal the inline boot-watchdog (app.blade.php) that boot succeeded.
    window.__bootOK = true;
  },
  progress: {
    color: '#4B5563',
  },
});
